import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiGet, apiPost } from '../../../services/api';
import { parseApiErrors, validateStep } from './postJobWizardSchema';
import { jobToWizardForm } from './postJobWizardMapping';

export function usePostJobWizardState({
  token,
  jobId,
  setJobId,
  form,
  setForm,
  persistDraft,
  ensureJobId,
  bindFormRef,
  setSaveError,
  setSaving,
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftParam = searchParams.get('draft');

  const [step, setStep] = useState(1);
  const [categories, setCategories] = useState([]);
  const [packages, setPackages] = useState([]);
  const [totalCredits, setTotalCredits] = useState(0);
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [stepErrors, setStepErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryDraft, setRecoveryDraft] = useState(null);
  const [initError, setInitError] = useState('');
  const [reactivationRequired, setReactivationRequired] = useState(false);
  const [reactivationFee, setReactivationFee] = useState(0);
  const [companyUnapproved, setCompanyUnapproved] = useState(false);
  const [roleDenied, setRoleDenied] = useState(false);

  useEffect(() => {
    bindFormRef(form);
  }, [form, bindFormRef]);

  const loadPackages = useCallback(async () => {
    if (!token) return;
    const res = await apiGet('/api/v1/company/packages', token);
    if (!res.ok) return;
    const data = await res.json();
    const rows = data.data ?? [];
    setPackages(rows);
    setTotalCredits(data.meta?.total_credits ?? 0);
    if (rows.length > 0) {
      setSelectedPackageId(rows[0].id);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    (async () => {
      setLoading(true);
      setInitError('');
      try {
        const statusRes = await apiGet('/api/v1/company/onboarding/status', token);
        if (statusRes.ok) {
          const statusData = await statusRes.json();
          if (!cancelled && statusData.data?.company && statusData.data.company.is_approved === false) {
            setCompanyUnapproved(true);
            return;
          }
          if (!cancelled && statusData.data?.company && statusData.data.company.can_manage_jobs === false) {
            setRoleDenied(true);
            return;
          }
        }

        const reactivationRes = await apiGet('/api/v1/company/reactivation-status', token);
        if (reactivationRes.ok) {
          const reactivationData = await reactivationRes.json();
          if (!cancelled && reactivationData.data?.required) {
            setReactivationRequired(true);
            setReactivationFee(reactivationData.data.fee ?? 0);
            return;
          }
        }

        const catRes = await apiGet('/api/v1/categories');
        const catData = await catRes.json();
        if (!cancelled) setCategories(catData.data ?? []);

        await loadPackages();

        if (draftParam) {
          const jobRes = await apiGet(`/api/v1/company/jobs/${draftParam}`, token);
          if (jobRes.ok) {
            const jobData = await jobRes.json();
            if (!cancelled && jobData.data?.status === 'draft') {
              setJobId(jobData.data.id);
              setForm(jobToWizardForm(jobData.data));
            }
          }
        } else {
          const latestRes = await apiGet('/api/v1/company/jobs/draft/latest', token);
          if (latestRes.ok) {
            const latestData = await latestRes.json();
            if (!cancelled && latestData.data?.id) {
              setRecoveryDraft(latestData.data);
              setShowRecovery(true);
            }
          }
        }
      } catch {
        if (!cancelled) setInitError('Could not load the job posting wizard.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, draftParam, loadPackages, setJobId, setForm]);

  const updateForm = useCallback(
    (field, value) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    [setForm]
  );

  const updateSeniority = useCallback(
    (value, checked) => {
      setForm((prev) => ({
        ...prev,
        senioritySelection: { ...prev.senioritySelection, [value]: checked },
      }));
    },
    [setForm]
  );

  const handleContinueDraft = useCallback(() => {
    if (!recoveryDraft) return;
    setJobId(recoveryDraft.id);
    setForm(jobToWizardForm(recoveryDraft));
    setShowRecovery(false);
  }, [recoveryDraft, setJobId, setForm]);

  const handleStartFresh = useCallback(() => {
    setRecoveryDraft(null);
    setShowRecovery(false);
  }, []);

  const goNext = useCallback(async () => {
    const errors = validateStep(step, form);
    setStepErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    try {
      await ensureJobId();
      await persistDraft(false, form);
      if (step < 5) {
        setStep((s) => s + 1);
        if (step === 4) await loadPackages();
      }
    } catch {
      setSaveError('Could not save progress. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [step, form, ensureJobId, persistDraft, loadPackages, setSaveError, setSaving]);

  const goBack = useCallback(async () => {
    if (step <= 1) return;
    setStepErrors({});
    if (jobId) await persistDraft(true, form);
    setStep((s) => s - 1);
  }, [step, jobId, form, persistDraft]);

  const handleSubmit = useCallback(async () => {
    const allErrors = {};
    for (let s = 1; s <= 4; s += 1) {
      Object.assign(allErrors, validateStep(s, form));
    }
    setStepErrors(allErrors);
    if (Object.keys(allErrors).length > 0) {
      setStepErrors((prev) => ({
        ...prev,
        submit: 'Complete all required steps before submitting.',
      }));
      return;
    }

    if (totalCredits < 1) return;

    setSubmitting(true);
    setStepErrors({});
    try {
      const id = await ensureJobId();
      await persistDraft(true, form);
      const body = selectedPackageId ? { package_id: selectedPackageId } : {};
      const res = await apiPost(`/api/v1/company/jobs/${id}/submit`, body, token);
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) {
        navigate('/login', { replace: true });
        return;
      }
      if (res.status === 422) {
        setStepErrors(parseApiErrors(data));
        return;
      }
      if (!res.ok) {
        setStepErrors({ submit: 'Submit failed. Please try again.' });
        return;
      }
      navigate('/company/dashboard?tab=jobs&submitted=1', { replace: true });
    } catch {
      setStepErrors({ submit: 'Submit failed. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  }, [form, totalCredits, ensureJobId, persistDraft, selectedPackageId, token, navigate]);

  const refreshReactivationStatus = useCallback(async () => {
    if (!token) return;
    const res = await apiGet('/api/v1/company/reactivation-status', token);
    if (!res.ok) return;
    const data = await res.json();
    if (data?.data?.required === false) {
      setReactivationRequired(false);
      setReactivationFee(0);
      setLoading(true);
      setInitError('');
      try {
        const catRes = await apiGet('/api/v1/categories');
        const catData = await catRes.json();
        setCategories(catData.data ?? []);
        await loadPackages();
        const latestRes = await apiGet('/api/v1/company/jobs/draft/latest', token);
        if (latestRes.ok) {
          const latestData = await latestRes.json();
          if (latestData.data?.id) {
            setRecoveryDraft(latestData.data);
            setShowRecovery(true);
          }
        }
      } catch {
        setInitError('Could not load the job posting wizard.');
      } finally {
        setLoading(false);
      }
    }
  }, [token, loadPackages, setRecoveryDraft, setShowRecovery]);

  return {
    step,
    form,
    jobId,
    categories,
    packages,
    totalCredits,
    selectedPackageId,
    setSelectedPackageId,
    stepErrors,
    loading,
    submitting,
    showRecovery,
    recoveryDraft,
    initError,
    reactivationRequired,
    reactivationFee,
    companyUnapproved,
    roleDenied,
    refreshReactivationStatus,
    updateForm,
    updateSeniority,
    goNext,
    goBack,
    handleSubmit,
    handleContinueDraft,
    handleStartFresh,
  };
}
